// Remembering which promo banners the customer has closed, for 24 hours each.
//
// See docs/promo-banners.md section 7. The cookie is read SERVER-side - the component filters
// dismissed banners out before rendering, so a closed banner never flashes up and disappears -
// which is why it is on the encryptCookies except list in bootstrap/app.php. This file and
// App\Models\PromoBanner are the only two things that touch it, and they have to agree about
// its shape.
//
// Shape: comma-separated {id}:{version}:{expires} triples, all three integers.
//
//   id       - the banner
//   version  - its updated_at as a unix time, so an admin editing the copy brings the banner
//              back for someone who closed the previous wording
//   expires  - unix time this dismissal lapses, 24 hours after the click
//
// EACH TRIPLE CARRIES ITS OWN EXPIRY, rather than leaning on the cookie's max-age. One cookie
// holds every dismissal and is rewritten whenever another banner is closed, so a cookie-wide
// lifetime would silently extend older dismissals every time the customer closed a new one:
// close banner B at hour 23 and banner A would quietly get another 24 hours.

// Must match App\Models\PromoBanner::DISMISS_COOKIE.
const COOKIE_NAME = 'promo_dismissed';

const DAY_IN_SECONDS = 24 * 60 * 60;

function nowInSeconds() {
    return Math.floor(Date.now() / 1000);
}

/**
 * The triples currently in the cookie, dropping anything malformed or lapsed.
 *
 * Mirrors PromoBanner::dismissedIds()'s strictness deliberately. This half is only pruning its
 * own writes, so it could afford to be lax - but the two reading the same string differently is
 * how they drift, and the server's copy is the one that decides what renders.
 *
 * @returns {Array<{id: number, version: number, expires: number}>}
 */
function readDismissals() {
    const match = document.cookie.match(new RegExp(`(?:^|; )${COOKIE_NAME}=([^;]*)`));

    if (! match) {
        return [];
    }

    const now = nowInSeconds();

    return decodeURIComponent(match[1])
        .split(',')
        .map((triple) => {
            const parts = triple.split(':');

            if (parts.length !== 3) {
                return null;
            }

            // Number() rather than parseInt(): parseInt('7abc') is 7, which would accept a
            // hand-edited value the server's ctype_digit() check then rejects, and the two
            // must agree. Number('7abc') is NaN.
            const [id, version, expires] = parts.map(Number);

            if (! Number.isInteger(id) || ! Number.isInteger(version) || ! Number.isInteger(expires)) {
                return null;
            }

            return { id, version, expires };
        })
        .filter((dismissal) => dismissal !== null && dismissal.expires > now);
}

function writeDismissals(dismissals) {
    const value = dismissals
        .map(({ id, version, expires }) => `${id}:${version}:${expires}`)
        .join(',');

    const attributes = [
        `${COOKIE_NAME}=${encodeURIComponent(value)}`,
        'path=/',
        // The outer bound only - each triple's own expires is what actually decides. Set from
        // the longest-lived dismissal so the cookie outlives everything in it.
        `max-age=${DAY_IN_SECONDS}`,
        'SameSite=Lax',
    ];

    // Omitted on http so local development over a plain connection still stores it.
    if (window.location.protocol === 'https:') {
        attributes.push('Secure');
    }

    document.cookie = attributes.join('; ');
}

/**
 * Record that this banner has been closed, for the next 24 hours.
 *
 * Replaces any existing dismissal of the same banner rather than adding a second, so a banner
 * edited and closed again does not leave its old version's triple behind to grow the cookie.
 *
 * @param {number} id
 * @param {number} version The banner's updated_at as a unix time.
 */
export function rememberDismissal(id, version) {
    if (! Number.isInteger(id) || ! Number.isInteger(version)) {
        return;
    }

    const kept = readDismissals().filter((dismissal) => dismissal.id !== id);

    kept.push({ id, version, expires: nowInSeconds() + DAY_IN_SECONDS });

    writeDismissals(kept);
}
